import { useMemo } from "react";
import { ServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import { MedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";
import { AppointmentType } from "@/types";

type Provider = MedspaProvidersQuery["medspaByPk"]["userMedspas"][number];

type ServiceMenuItem =
  ServicesForBookingAppointmentQuery["medspaServiceMenuItem"][number];

type AppointmentService = {
  serviceMenuItem: ServiceMenuItem;
};

/**
 * Returns a set of service menu item ids that are disabled because they are not eligible for the selected providers.
 */
const useProviderBasedDisabledServicesIds = ({
  availableServices,
  selectedProviders,
}: {
  availableServices: ServiceMenuItem[];
  selectedProviders: Provider[];
}) =>
  useMemo(() => {
    const result = new Set<string>();
    if (selectedProviders.length === 0) {
      return result;
    }

    const selectedProviderUserMedspaIds = new Set(
      selectedProviders.map(({ id }) => id)
    );

    for (const serviceMenuItem of availableServices) {
      const isEligible = serviceMenuItem.eligibleProviders.some((provider) =>
        selectedProviderUserMedspaIds.has(provider.userMedspa.id)
      );
      if (!isEligible) {
        result.add(serviceMenuItem.id);
      }
    }

    return result;
  }, [availableServices, selectedProviders]);

/**
 * Returns a set of service menu item ids that are disabled based on selected appointment services appointment type.
 *
 * If every appointment service has telehealth type, then all in-person services are disabled.
 * If there is at least one appointment service with in-person type, then all telehealth services are disabled.
 *
 * This hook meant to be used in components where it's impossible to mix appointmentTypes, we can safely assume
 * that all appointmentServices has either telehealth or in-person type.
 */
const useTelehealthBasedDisabledServicesIds = ({
  appointmentServices,
  availableServices,
}: {
  appointmentServices: AppointmentService[];
  availableServices: ServiceMenuItem[];
}) =>
  useMemo(() => {
    if (!appointmentServices.length) {
      return new Set<string>();
    }

    const isAppointmentTelehealth = appointmentServices.every(
      ({ serviceMenuItem }) =>
        serviceMenuItem.appointmentType === AppointmentType.TELEHEALTH
    );

    const disabledType = isAppointmentTelehealth
      ? AppointmentType.IN_PERSON
      : AppointmentType.TELEHEALTH;

    const telehealthBasedDisabledServicesIds = availableServices
      .filter((service) => service.appointmentType === disabledType)
      .map((service) => service.id);

    return new Set(telehealthBasedDisabledServicesIds);
  }, [appointmentServices, availableServices]);

export const useDisabledServicesIds = ({
  appointmentServices,
  availableServices,
  selectedProviders,
}: {
  appointmentServices: AppointmentService[];
  availableServices: ServiceMenuItem[];
  selectedProviders: Provider[];
}) => {
  const providerBasedDisabledServicesIds = useProviderBasedDisabledServicesIds({
    availableServices,
    selectedProviders,
  });

  const telehealthBasedDisabledServicesIds =
    useTelehealthBasedDisabledServicesIds({
      appointmentServices,
      availableServices,
    });

  return useMemo(
    () =>
      new Set([
        ...providerBasedDisabledServicesIds,
        ...telehealthBasedDisabledServicesIds,
      ]),
    [providerBasedDisabledServicesIds, telehealthBasedDisabledServicesIds]
  );
};
